import { AxiosError } from 'axios';
import api from './api';
import { CalendarStatus, GCalEvent, CalendarPushResult, PinnedEvent } from '../types/types';

const CALENDAR_SCOPE = 'https://www.googleapis.com/auth/calendar.events';

/**
 * Exchange a GIS authorization code for OAuth tokens server-side.
 * Sends the code to the backend, which calls Google's token endpoint
 * and stores access_token + refresh_token in UserCalendarToken.
 */
export async function storeCalendarToken(
  authorizationCode: string,
  googleEmail: string
): Promise<CalendarStatus> {
  const response = await api.post<CalendarStatus>('/calendar/connect/', {
    authorization_code: authorizationCode,
    google_email: googleEmail,
  });
  return response.data;
}

/**
 * Trigger Google Identity Services popup to obtain an authorization code,
 * then exchange it server-side for access + refresh tokens.
 *
 * Requires REACT_APP_GOOGLE_CLIENT_ID in .env and the GIS script loaded in index.html.
 * Uses access_type=offline (implicit in the code flow) so the backend receives a refresh_token.
 */
export function connectGoogleCalendar(userEmail: string): Promise<CalendarStatus> {
  return new Promise((resolve, reject) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const google = (window as any).google;
    if (!google?.accounts?.oauth2) {
      reject(new Error('Google Identity Services not loaded'));
      return;
    }
    const client = google.accounts.oauth2.initCodeClient({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      scope: CALENDAR_SCOPE,
      ux_mode: 'popup',
      callback: async (response: { code?: string; error?: string }) => {
        if (response.error || !response.code) {
          reject(new Error(response.error ?? 'No authorization code returned'));
          return;
        }
        try {
          const calStatus = await storeCalendarToken(response.code, userEmail);
          resolve(calStatus);
        } catch (e) {
          reject(e);
        }
      },
    });
    client.requestCode();
  });
}

export async function getCalendarStatus(): Promise<CalendarStatus> {
  const response = await api.get<CalendarStatus>('/calendar/status/');
  return response.data;
}

export async function disconnectGoogleCalendar(): Promise<void> {
  await api.delete('/calendar/disconnect/');
}

export async function pushToCalendar(): Promise<CalendarPushResult> {
  const response = await api.post<CalendarPushResult>('/calendar/push/');
  return response.data;
}

export async function getPinnedEvents(): Promise<PinnedEvent[]> {
  try {
    const response = await api.get<{ pinned_events: PinnedEvent[] }>('/calendar/pinned-events/');
    return response.data?.pinned_events ?? [];
  } catch {
    return [];
  }
}

export async function getCalendarEvents(daysAhead = 30): Promise<GCalEvent[]> {
  try {
    const response = await api.get<{ events: GCalEvent[] }>(
      `/calendar/events/?days_ahead=${daysAhead}`
    );
    return response.data?.events ?? [];
  } catch (err) {
    const axiosErr = err as AxiosError<{ detail?: string }>;
    const detail = axiosErr.response?.data?.detail;
    if (axiosErr.response?.status === 401 && detail === 'calendar_token_expired') {
      throw { code: 'calendar_token_expired' };
    }
    if (axiosErr.response?.status === 502 && detail === 'calendar_api_error') {
      throw { code: 'calendar_api_error' };
    }
    return [];
  }
}
